/**
 * Program Intelligence Map View Component
 * Interactive map showing program locations with markers
 * Uses Mapbox GL JS for visualization
 */

import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { MapPin, TrendingUp, Users, DollarSign, Building2 } from 'lucide-react';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// Set Mapbox access token from environment variable
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_API_KEY ?? '';

interface ProgramIntelligence {
  id: number;
  program_name: string;
  discovered_sponsor_name: string;
  discovered_sponsor_type: string;
  sponsor_headquarters_state: string;
  program_type: string;
  description: string;
  target_enrollment: number;
  service_counties: string[];
  distance_miles?: number;
  closest_county?: string;
  enrollment_growth_rate_12mo?: number;
  enrollment_trend_direction?: 'growing' | 'declining' | 'stable';
  application_deadline?: string;
  base_reimbursement_rate?: number;
  total_program_budget?: number;
}

interface ProgramMapViewProps {
  programs: ProgramIntelligence[];
  userLocation?: string;
}

const ProgramMapView: React.FC<ProgramMapViewProps> = ({ programs, userLocation }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<ProgramIntelligence | null>(null);

  // CA County coordinates for mapping (all 58 counties)
  const CA_COUNTY_COORDS: Record<string, [number, number]> = {
    'Alameda': [37.8044, -122.2712],
    'Alpine': [38.5969, -119.8138],
    'Amador': [38.3496, -120.6538],
    'Butte': [39.6292, -121.5986],
    'Calaveras': [38.1908, -120.5391],
    'Colusa': [39.1794, -122.2411],
    'Contra Costa': [37.9161, -121.9511],
    'Del Norte': [41.7437, -124.1360],
    'El Dorado': [38.7646, -120.5338],
    'Fresno': [36.7378, -119.7871],
    'Glenn': [39.5985, -122.3892],
    'Humboldt': [40.7450, -123.8695],
    'Imperial': [33.0114, -115.3626],
    'Inyo': [36.5784, -117.4078],
    'Kern': [35.3733, -118.4412],
    'Kings': [36.0822, -119.8189],
    'Lake': [39.0840, -122.7594],
    'Lassen': [40.6618, -120.5572],
    'Los Angeles': [34.0522, -118.2437],
    'Madera': [37.1358, -119.7707],
    'Marin': [38.0834, -122.7633],
    'Mariposa': [37.4849, -119.9663],
    'Mendocino': [39.4318, -123.3494],
    'Merced': [37.1885, -120.7398],
    'Modoc': [41.5888, -120.7394],
    'Mono': [37.9410, -118.9601],
    'Monterey': [36.6002, -121.8947],
    'Napa': [38.5025, -122.2654],
    'Nevada': [39.3080, -120.7989],
    'Orange': [33.7175, -117.8311],
    'Placer': [39.0916, -120.8039],
    'Plumas': [40.0024, -120.8326],
    'Riverside': [33.7534, -116.0000],
    'Sacramento': [38.5816, -121.4944],
    'San Benito': [36.5761, -121.0355],
    'San Bernardino': [34.1083, -117.2898],
    'San Diego': [32.7157, -117.1611],
    'San Francisco': [37.7749, -122.4194],
    'San Joaquin': [37.9577, -121.2908],
    'San Luis Obispo': [35.3102, -120.2620],
    'San Mateo': [37.5630, -122.3255],
    'Santa Barbara': [34.5708, -119.9578],
    'Santa Clara': [37.3541, -121.9552],
    'Santa Cruz': [36.9741, -122.0308],
    'Shasta': [40.5865, -122.3917],
    'Sierra': [39.5780, -120.5173],
    'Siskiyou': [41.5986, -122.4719],
    'Solano': [38.2441, -121.9618],
    'Sonoma': [38.5780, -122.9888],
    'Stanislaus': [37.6391, -120.9970],
    'Sutter': [39.0243, -121.6919],
    'Tehama': [40.1252, -122.2486],
    'Trinity': [40.6649, -123.1189],
    'Tulare': [36.2077, -118.8648],
    'Tuolumne': [37.9577, -120.2379],
    'Ventura': [34.3705, -119.1391],
    'Yolo': [38.6899, -121.9018],
    'Yuba': [39.1404, -121.6169]
  };

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Initialize map
    mapboxgl.accessToken = MAPBOX_TOKEN;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-119.4179, 36.7783], // Center of California
      zoom: 5.5,
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    if (!map.current) return;

    // Clear existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Add markers for each program
    programs.forEach(program => {
      // Get coordinates for the program's closest county
      const county = program.closest_county || program.service_counties?.[0];
      if (!county) return;

      const coords = CA_COUNTY_COORDS[county];
      if (!coords) return;

      // Create marker element
      const el = document.createElement('div');
      el.className = 'program-marker';
      el.style.width = '30px';
      el.style.height = '30px';
      el.style.borderRadius = '50%';
      el.style.cursor = 'pointer';
      el.style.border = '3px solid white';
      el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';

      // Color based on enrollment trend
      if (program.enrollment_trend_direction === 'growing') {
        el.style.backgroundColor = '#10b981'; // green
      } else if (program.enrollment_trend_direction === 'declining') {
        el.style.backgroundColor = '#ef4444'; // red
      } else {
        el.style.backgroundColor = '#8b5cf6'; // purple
      }

      // Create popup content
      const popupContent = `
        <div style="padding: 8px; min-width: 250px;">
          <h3 style="font-weight: bold; font-size: 14px; margin-bottom: 4px; color: #111;">
            ${program.program_name}
          </h3>
          <p style="font-size: 12px; color: #666; margin-bottom: 8px;">
            ${program.discovered_sponsor_name}
          </p>
          ${program.target_enrollment ? `
            <div style="font-size: 12px; color: #444; margin-bottom: 4px;">
              <strong>{t('marketplace.target_enrollment', 'Target Enrollment:')}</strong> ${program.target_enrollment.toLocaleString()} members
            </div>
          ` : ''}
          ${program.enrollment_growth_rate_12mo !== undefined && program.enrollment_growth_rate_12mo !== null ? `
            <div style="font-size: 12px; color: ${program.enrollment_growth_rate_12mo > 0 ? '#10b981' : '#ef4444'}; margin-bottom: 4px;">
              <strong>12mo Growth:</strong> ${program.enrollment_growth_rate_12mo > 0 ? '+' : ''}${program.enrollment_growth_rate_12mo.toFixed(1)}%
            </div>
          ` : ''}
          ${program.distance_miles ? `
            <div style="font-size: 12px; color: #666; margin-bottom: 4px;">
              <strong>{t('marketplace.distance', 'Distance:')}</strong> ${program.distance_miles} miles away
            </div>
          ` : ''}
          ${program.application_deadline ? `
            <div style="font-size: 12px; color: #f59e0b; margin-bottom: 4px;">
              <strong>{t('marketplace.deadline', 'Deadline:')}</strong> ${fmt.short(program.application_deadline)}
            </div>
          ` : ''}
          <button
            class="view-details-btn"
            data-program-id="${program.id}"
            style="margin-top: 8px; padding: 6px 12px; background-color: #8b5cf6; color: white; border: none; border-radius: 6px; font-size: 12px; cursor: pointer; width: 100%;"
          >
            {t('marketplace.view_details', 'View Details')}
          </button>
        </div>
      `;

      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        maxWidth: '300px'
      }).setHTML(popupContent);

      // Create and add marker
      const marker = new mapboxgl.Marker(el)
        .setLngLat([coords[1], coords[0]])
        .setPopup(popup)
        .addTo(map.current!);

      markers.current.push(marker);
    });

    // Handle program selection from popup buttons using event delegation
    const handlePopupClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if clicked element is the View Details button
      if (target.classList.contains('view-details-btn')) {
        const programId = parseInt(target.getAttribute('data-program-id') || '0', 10);
        const program = programs.find(p => p.id === programId);
        if (program) {
          setSelectedProgram(program);
          // Scroll to detail panel
          setTimeout(() => {
            const detailPanel = document.querySelector('.selected-program-detail');
            if (detailPanel) {
              detailPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }, 100);
        }
      }
    };

    // Add click listener to document for popup buttons
    document.addEventListener('click', handlePopupClick);

    return () => {
      document.removeEventListener('click', handlePopupClick);
    };
  }, [programs]);

  // Add user location marker if provided
  useEffect(() => {
    if (!map.current || !userLocation) return;

    // TODO: Geocode user location and add marker
    // This would require calling the backend geocoding API

  }, [userLocation]);

  return (
    <div className="space-y-4">
      {/* Map Container */}
      <Card className="overflow-hidden">
        <div
          ref={mapContainer}
          className="w-full h-[600px]"
          style={{ minHeight: '600px' }}
        />
      </Card>

      {/* Map Legend */}
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('marketplace.map_legend', 'Map Legend')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-green-500 border-2 border-white shadow" />
            <span className="text-sm text-gray-600">{t('marketplace.growing_enrollment', 'Growing Enrollment')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-purple-600 border-2 border-white shadow" />
            <span className="text-sm text-gray-600">{t('marketplace.stable_enrollment', 'Stable Enrollment')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-red-500 border-2 border-white shadow" />
            <span className="text-sm text-gray-600">{t('marketplace.declining_enrollment', 'Declining Enrollment')}</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          {t('marketplace.click_on_any_marker_to', 'Click on any marker to view program details. Marker locations represent service counties.')}
        </p>
      </Card>

      {/* Selected Program Detail Panel */}
      {selectedProgram && (
        <Card className="p-6 selected-program-detail">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900">{selectedProgram.program_name}</h3>
              <p className="text-sm text-gray-600">{selectedProgram.discovered_sponsor_name}</p>
            </div>
            <button
              onClick={() => setSelectedProgram(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {selectedProgram.target_enrollment && (
              <div className="flex items-start gap-2">
                <Users className="h-4 w-4 text-purple-600 mt-1" />
                <div>
                  <div className="text-xs text-gray-500">{t('marketplace.target_enrollment_1', 'Target Enrollment')}</div>
                  <div className="text-sm font-semibold">
                    {selectedProgram.target_enrollment.toLocaleString()}
                  </div>
                </div>
              </div>
            )}

            {selectedProgram.enrollment_growth_rate_12mo !== undefined && selectedProgram.enrollment_growth_rate_12mo !== null && (
              <div className="flex items-start gap-2">
                <TrendingUp className="h-4 w-4 text-green-600 mt-1" />
                <div>
                  <div className="text-xs text-gray-500">12mo Growth</div>
                  <div className={`text-sm font-semibold ${
                    selectedProgram.enrollment_growth_rate_12mo > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {selectedProgram.enrollment_growth_rate_12mo > 0 ? '+' : ''}
                    {selectedProgram.enrollment_growth_rate_12mo.toFixed(1)}%
                  </div>
                </div>
              </div>
            )}

            {selectedProgram.distance_miles && (
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-blue-600 mt-1" />
                <div>
                  <div className="text-xs text-gray-500">{t('marketplace.distance_1', 'Distance')}</div>
                  <div className="text-sm font-semibold">{selectedProgram.distance_miles} mi</div>
                </div>
              </div>
            )}

            {selectedProgram.total_program_budget && (
              <div className="flex items-start gap-2">
                <DollarSign className="h-4 w-4 text-amber-600 mt-1" />
                <div>
                  <div className="text-xs text-gray-500">{t('marketplace.budget', 'Budget')}</div>
                  <div className="text-sm font-semibold">
                    ${(selectedProgram.total_program_budget / 1000000).toFixed(1)}M
                  </div>
                </div>
              </div>
            )}
          </div>

          {selectedProgram.description && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 line-clamp-3">{selectedProgram.description}</p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default ProgramMapView;
